import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { BayDefinition, BayOrientation } from "../../../types";

interface HangarBayEditorProps {
  hangarWidth: number;
  hangarDepth: number;
  bayDefinitions: BayDefinition[]; // Controlled prop (not initial)
  bayOrientation: BayOrientation; // Controlled prop (not initial)
  onChange: (bays: BayDefinition[], orientation: BayOrientation) => void;
}

/**
 * Controlled editor component for hangar bay definitions
 * Supports lateral (side-by-side) and longitudinal (front-to-back) orientations
 *
 * This is a CONTROLLED component - it does not maintain its own state.
 * Parent must pass bayDefinitions and bayOrientation props.
 *
 * Features:
 * - Radio button orientation selector
 * - List editor for bay dimensions and heights
 * - Real-time validation (total dimension, clear < eave)
 * - Add/remove bays (minimum 1 bay required)
 *
 * @example
 * <HangarBayEditor
 *   hangarWidth={260}
 *   hangarDepth={120}
 *   bayDefinitions={bayDefinitions}
 *   bayOrientation={bayOrientation}
 *   onChange={(bays, orientation) => {
 *     setBayDefinitions(bays);
 *     setBayOrientation(orientation);
 *   }}
 * />
 */
export const HangarBayEditor: React.FC<HangarBayEditorProps> = ({
  hangarWidth,
  hangarDepth,
  bayDefinitions = [], // Default to empty array
  bayOrientation = "lateral", // Default to lateral
  onChange,
}) => {
  // Use props directly (controlled component - no internal state)
  const bays =
    bayDefinitions.length > 0
      ? bayDefinitions
      : [{ width: hangarWidth, eave_height: 30, clear_height: 28 }];
  const orientation = bayOrientation;

  // Calculate totals
  const totalWidth = bays.reduce((sum, bay) => sum + (bay.width || 0), 0);
  const totalDepth = bays.reduce((sum, bay) => sum + (bay.depth || 0), 0);

  const isLateral = orientation === "lateral";
  const totalDimension = isLateral ? totalWidth : totalDepth;
  const hangarDimension = isLateral ? hangarWidth : hangarDepth;

  // Calculate coverage metrics
  const coverageGap = hangarDimension - totalDimension;
  const isComplete = Math.abs(coverageGap) < 0.1; // Exact match (±0.1ft tolerance)
  const isOverflow = coverageGap < -0.1; // Exceeds hangar
  const isUndercovered = coverageGap > 0.1; // Gap exists
  const coveragePercent = (totalDimension / hangarDimension) * 100;

  const handleOrientationChange = (newOrientation: BayOrientation) => {
    // Reset bays with appropriate dimension
    const newBays: BayDefinition[] = [
      {
        [newOrientation === "lateral" ? "width" : "depth"]:
          newOrientation === "lateral" ? hangarWidth : hangarDepth,
        eave_height: 30,
        clear_height: 28,
      },
    ];
    onChange(newBays, newOrientation);
  };

  const handleBayChange = (
    index: number,
    field: keyof BayDefinition,
    value: number
  ) => {
    const newBays = [...bays];
    newBays[index] = { ...newBays[index], [field]: value };
    onChange(newBays, orientation);
  };

  const handleAddBay = () => {
    // Calculate remaining dimension to suggest smart default
    const remaining = hangarDimension - totalDimension;
    const suggestedDimension =
      remaining > 0.1 ? remaining : isLateral ? 50 : 40;

    const newBay: BayDefinition = {
      [isLateral ? "width" : "depth"]: suggestedDimension,
      eave_height: bays.length > 0 ? bays[bays.length - 1].eave_height : 30,
      clear_height: bays.length > 0 ? bays[bays.length - 1].clear_height : 28,
    };
    const newBays = [...bays, newBay];
    onChange(newBays, orientation);
  };

  const handleFillRemainder = () => {
    const remaining = hangarDimension - totalDimension;
    if (remaining <= 0.1 || bays.length === 0) return;

    const lastBay = bays[bays.length - 1];
    const currentDimension = isLateral
      ? lastBay.width || 0
      : lastBay.depth || 0;
    const newDimension = currentDimension + remaining;

    handleBayChange(
      bays.length - 1,
      isLateral ? "width" : "depth",
      newDimension
    );
  };

  const handleRemoveBay = (index: number) => {
    if (bays.length === 1) return; // Keep at least one bay
    const newBays = bays.filter((_, i) => i !== index);
    onChange(newBays, orientation);
  };

  return (
    <Box>
      {/* Orientation Selector with inline tooltips */}
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          value={orientation}
          onChange={(e) =>
            handleOrientationChange(e.target.value as BayOrientation)
          }
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <FormControlLabel
              value="lateral"
              control={<Radio />}
              label="Lateral (Side-by-Side)"
            />
            <Tooltip
              title={
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    whiteSpace: "pre",
                  }}
                >
                  {`┌──┬───┬──┐
│A │ B │C │
│  │   │  │
└──┴───┴──┘
Bays divide left→right
Example: 3 bays of 80ft, 100ft, 80ft`}
                </Box>
              }
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormControlLabel
              value="longitudinal"
              control={<Radio />}
              label="Longitudinal (Front-to-Back)"
            />
            <Tooltip
              title={
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    whiteSpace: "pre",
                  }}
                >
                  {`┌─────────┐
│    A    │
├─────────┤
│    B    │
└─────────┘
Bays divide front→back
Example: 2 bays of 60ft, 60ft`}
                </Box>
              }
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        </RadioGroup>
      </FormControl>

      {/* Bay List */}
      <Stack spacing={1}>
        {bays.map((bay, index) => {
          // Calculate cumulative range for this bay
          const dimension = isLateral ? bay.width || 0 : bay.depth || 0;
          const rangeStart = bays
            .slice(0, index)
            .reduce(
              (sum, b) => sum + (isLateral ? b.width || 0 : b.depth || 0),
              0
            );
          const rangeEnd = rangeStart + dimension;

          return (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <Tooltip
                title={`Bay covers ${rangeStart.toFixed(
                  1
                )}ft - ${rangeEnd.toFixed(1)}ft`}
              >
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 80,
                    fontFamily: "monospace",
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  {rangeStart.toFixed(0)}-{rangeEnd.toFixed(0)}ft
                </Typography>
              </Tooltip>
              <TextField
                type="number"
                size="small"
                label={isLateral ? "Width (ft)" : "Depth (ft)"}
                value={isLateral ? bay.width || "" : bay.depth || ""}
                onChange={(e) =>
                  handleBayChange(
                    index,
                    isLateral ? "width" : "depth",
                    parseFloat(e.target.value) || 0
                  )
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 120 }}
              />
              <TextField
                type="number"
                size="small"
                label="Eave Ht (ft)"
                value={bay.eave_height}
                onChange={(e) =>
                  handleBayChange(
                    index,
                    "eave_height",
                    parseFloat(e.target.value) || 0
                  )
                }
                inputProps={{ min: 0, step: 0.5 }}
                sx={{ width: 100 }}
              />
              <TextField
                type="number"
                size="small"
                label="Clear Ht (ft)"
                value={bay.clear_height}
                onChange={(e) =>
                  handleBayChange(
                    index,
                    "clear_height",
                    parseFloat(e.target.value) || 0
                  )
                }
                inputProps={{ min: 0, step: 0.5 }}
                error={bay.clear_height > bay.eave_height}
                sx={{ width: 100 }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveBay(index)}
                disabled={bays.length === 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>

      {/* Coverage Summary with Segmented Progress Bar */}
      <Box sx={{ mt: 2 }}>
        {/* Segmented Progress Bar - Each bay gets a colored segment */}
        <Box
          sx={{
            height: 24,
            border: "1px solid",
            borderColor: isComplete
              ? "success.main"
              : isOverflow
              ? "error.main"
              : "warning.main",
            borderRadius: 1,
            overflow: "hidden",
            display: "flex",
            mb: 1,
            bgcolor: "#f5f5f5",
          }}
        >
          {bays.map((bay, index) => {
            const dimension = isLateral ? bay.width || 0 : bay.depth || 0;
            const bayPercent = (dimension / hangarDimension) * 100;
            const rangeStart = bays
              .slice(0, index)
              .reduce(
                (sum, b) => sum + (isLateral ? b.width || 0 : b.depth || 0),
                0
              );
            const rangeEnd = rangeStart + dimension;

            // Color palette for bays (cycling through shades)
            const colors = [
              "#1976d2",
              "#42a5f5",
              "#64b5f6",
              "#90caf9",
              "#bbdefb",
            ];
            const bgColor = colors[index % colors.length];

            return (
              <Tooltip
                key={index}
                title={`Bay ${index + 1}: ${rangeStart.toFixed(
                  0
                )}-${rangeEnd.toFixed(0)}ft (${bayPercent.toFixed(1)}%)`}
              >
                <Box
                  sx={{
                    width: `${Math.min(bayPercent, 100)}%`,
                    bgcolor: bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    fontFamily: "Lexend Deca, sans-serif",
                    cursor: "help",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      filter: "brightness(1.1)",
                    },
                  }}
                >
                  {bayPercent > 10 && `${bayPercent.toFixed(0)}%`}
                </Box>
              </Tooltip>
            );
          })}

          {/* Overflow indicator (if exceeds 100%) */}
          {isOverflow && (
            <Box
              sx={{
                width: `${Math.min(
                  Math.abs(coverageGap / hangarDimension) * 100,
                  20
                )}%`,
                bgcolor: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.65rem",
                fontWeight: "bold",
                fontFamily: "Lexend Deca, sans-serif",
              }}
            >
              +{Math.abs(coverageGap).toFixed(0)}ft
            </Box>
          )}
        </Box>

        {/* Coverage Metrics */}
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Total: {totalDimension.toFixed(1)} ft / {hangarDimension.toFixed(1)}{" "}
            ft ({coveragePercent.toFixed(0)}%)
          </Typography>
          <Typography
            variant="caption"
            color={
              isComplete
                ? "success.main"
                : isOverflow
                ? "error.main"
                : "warning.main"
            }
            sx={{ fontWeight: "bold" }}
          >
            {isComplete
              ? "✓ Complete coverage"
              : isOverflow
              ? `⚠️ Exceeds by ${Math.abs(coverageGap).toFixed(1)}ft`
              : `⚠️ Missing ${coverageGap.toFixed(1)}ft`}
          </Typography>
        </Stack>

        {/* Uncovered Range Indicator */}
        {isUndercovered && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
          >
            Uncovered {isLateral ? "width" : "depth"}:{" "}
            {totalDimension.toFixed(1)}ft - {hangarDimension.toFixed(1)}ft
          </Typography>
        )}
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddBay}
          variant="outlined"
        >
          Add Bay
        </Button>

        {isUndercovered && bays.length > 0 && (
          <Button
            startIcon={<AutoFixHighIcon />}
            onClick={handleFillRemainder}
            variant="contained"
            color="success"
            size="small"
          >
            Fill Remainder ({coverageGap.toFixed(1)}ft)
          </Button>
        )}
      </Stack>
    </Box>
  );
};
